// Organização do trabalho e busca — Fase 14.
//
// A regra que atravessa este arquivo: **as ações passam por RPC, não por UPDATE
// direto.** Não é preferência de estilo — é o que faz o audit log existir. A
// policy de escrita de `inbox_audit_log` é só de service_role (um log que o
// autor da ação pode editar não é log), então um `update` do PostgREST muda a
// conversa e não deixa rastro nenhum.
//
// A exceção são as NOTAS: elas já nascem com autor e data, a tabela inteira é o
// registro, e a RLS de `inbox_notes` já garante que ninguém edita nota alheia.
// Um audit log de "criou nota" duplicaria a própria nota.

import { supabase } from '../../../src/lib/supabase';
import type { ChannelType, ConversaStatus, Direcao } from './tipos';

export interface Etiqueta {
  id: string;
  nome: string;
  cor: string;
}

export interface Nota {
  id: string;
  conversation_id: string;
  author_id: string;
  body: string;
  created_at: string;
  autor_nome?: string | null;
}

export interface RespostaRapida {
  id: string;
  atalho: string;
  body: string;
  /** null = serve para todos os canais. */
  channel_types: ChannelType[] | null;
}

export interface Membro {
  id: string;
  nome: string;
  papel: 'agente' | 'supervisor' | 'admin' | 'observador';
}

// ─── Ações com rastro ───────────────────────────────────────────────────────

/** `null` devolve a conversa para a fila de todos. */
export async function atribuirConversa(conversationId: string, userId: string | null): Promise<void> {
  const { data, error } = await supabase.rpc('inbox_atribuir_conversa', {
    p_conversation_id: conversationId,
    p_user_id: userId,
  });
  if (error) throw new Error(error.message);
  const r = data as { ok: boolean; erro?: string };
  if (!r?.ok) throw new Error(r?.erro ?? 'Não consegui atribuir a conversa.');
}

export async function mudarStatusConversa(conversationId: string, status: ConversaStatus): Promise<void> {
  const { data, error } = await supabase.rpc('inbox_mudar_status_conversa', {
    p_conversation_id: conversationId,
    p_status: status,
  });
  if (error) throw new Error(error.message);
  const r = data as { ok: boolean; erro?: string };
  if (!r?.ok) throw new Error(r?.erro ?? 'Não consegui mudar o status.');
}

export async function marcarEtiqueta(
  conversationId: string,
  tagId: string,
  aplicar: boolean,
): Promise<void> {
  const { data, error } = await supabase.rpc('inbox_marcar_tag', {
    p_conversation_id: conversationId,
    p_tag_id: tagId,
    p_aplicar: aplicar,
  });
  if (error) throw new Error(error.message);
  const r = data as { ok: boolean; erro?: string };
  if (!r?.ok) throw new Error(r?.erro ?? 'Não consegui alterar a etiqueta.');
}

// ─── Etiquetas ──────────────────────────────────────────────────────────────

export async function carregarEtiquetas(orgId: string): Promise<Etiqueta[]> {
  const { data, error } = await supabase
    .from('inbox_tags')
    .select('id, nome, cor')
    .eq('org_id', orgId)
    .order('nome');
  if (error) throw new Error(error.message);
  return (data ?? []) as Etiqueta[];
}

/**
 * As etiquetas de VÁRIAS conversas de uma vez.
 *
 * Uma consulta para a lista inteira, não uma por linha: a coluna da esquerda
 * carrega até 200 conversas, e uma consulta por conversa seriam 200 idas ao
 * banco só para desenhar bolinhas coloridas.
 */
export async function carregarEtiquetasDasConversas(
  conversationIds: string[],
): Promise<Record<string, string[]>> {
  if (conversationIds.length === 0) return {};
  const { data, error } = await supabase
    .from('inbox_conversation_tags')
    .select('conversation_id, tag_id')
    .in('conversation_id', conversationIds);
  if (error) throw new Error(error.message);

  const mapa: Record<string, string[]> = {};
  for (const linha of (data ?? []) as { conversation_id: string; tag_id: string }[]) {
    (mapa[linha.conversation_id] ??= []).push(linha.tag_id);
  }
  return mapa;
}

export async function criarEtiqueta(orgId: string, nome: string, cor: string): Promise<Etiqueta> {
  const { data, error } = await supabase
    .from('inbox_tags')
    .insert({ org_id: orgId, nome, cor })
    .select('id, nome, cor')
    .single();
  if (error) throw new Error(error.message);
  return data as Etiqueta;
}

export async function apagarEtiqueta(id: string): Promise<void> {
  const { error } = await supabase.from('inbox_tags').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Notas internas ─────────────────────────────────────────────────────────

export async function carregarNotas(conversationId: string): Promise<Nota[]> {
  const { data, error } = await supabase
    .from('inbox_notes')
    .select('id, conversation_id, author_id, body, created_at, autor:profiles!inbox_notes_author_id_fkey(full_name)')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[]).map((n) => ({
    id: n.id,
    conversation_id: n.conversation_id,
    author_id: n.author_id,
    body: n.body,
    created_at: n.created_at,
    autor_nome: n.autor?.full_name ?? null,
  }));
}

/**
 * Nota interna — o cliente NUNCA vê.
 *
 * A separação é o ponto: uma nota que vazasse para o canal seria um comentário
 * sobre o cliente indo para o próprio cliente. Por isso nota é tabela própria,
 * e não uma mensagem com uma flag — flag se esquece de filtrar em algum lugar.
 */
export async function criarNota(orgId: string, conversationId: string, body: string, autorId: string): Promise<void> {
  const { error } = await supabase
    .from('inbox_notes')
    .insert({ org_id: orgId, conversation_id: conversationId, body, author_id: autorId });
  if (error) throw new Error(error.message);
}

export async function apagarNota(id: string): Promise<void> {
  const { error } = await supabase.from('inbox_notes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Respostas rápidas ──────────────────────────────────────────────────────

export async function carregarRespostasRapidas(orgId: string): Promise<RespostaRapida[]> {
  const { data, error } = await supabase
    .from('inbox_quick_replies')
    .select('id, atalho, body, channel_types')
    .eq('org_id', orgId)
    .order('atalho');
  if (error) throw new Error(error.message);
  return (data ?? []) as RespostaRapida[];
}

/**
 * As que servem para este canal.
 *
 * `channel_types` nulo significa "todos" — e é o padrão de propósito: quem
 * cadastra uma resposta pensa no texto, não na matriz de canais. O filtro
 * existe para o caso oposto: uma resposta com assunto e assinatura de e-mail
 * não deve aparecer no WhatsApp.
 */
export function filtrarPorCanal(lista: RespostaRapida[], canal: ChannelType | null): RespostaRapida[] {
  if (!canal) return lista;
  return lista.filter((r) => !r.channel_types || r.channel_types.length === 0 || r.channel_types.includes(canal));
}

export async function salvarRespostaRapida(
  orgId: string,
  r: { id?: string; atalho: string; body: string; channel_types: ChannelType[] | null },
): Promise<void> {
  const linha = { org_id: orgId, atalho: r.atalho, body: r.body, channel_types: r.channel_types };
  const { error } = r.id
    ? await supabase.from('inbox_quick_replies').update(linha).eq('id', r.id)
    : await supabase.from('inbox_quick_replies').insert(linha);
  if (error) {
    throw new Error(
      error.code === '23505' ? 'Já existe uma resposta rápida com esse atalho.' : error.message,
    );
  }
}

export async function apagarRespostaRapida(id: string): Promise<void> {
  const { error } = await supabase.from('inbox_quick_replies').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ─── Membros ────────────────────────────────────────────────────────────────

export async function carregarMembrosDaOrg(orgId: string): Promise<Membro[]> {
  const { data, error } = await supabase
    .from('inbox_members')
    .select('user_id, role, profiles:profiles!inbox_members_user_id_fkey(full_name)')
    .eq('org_id', orgId);
  if (error) throw new Error(error.message);
  return ((data ?? []) as any[])
    .map((m) => ({
      id: String(m.user_id),
      nome: m.profiles?.full_name ?? 'Sem nome',
      papel: m.role as Membro['papel'],
    }))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

// ─── Busca ──────────────────────────────────────────────────────────────────

export interface AchadoBusca {
  message_id: string;
  conversation_id: string;
  direction: Direcao;
  channel_type: ChannelType;
  contato_nome: string | null;
  assunto: string | null;
  /** Trecho com o termo entre « ». */
  trecho: string;
  provider_timestamp: string;
}

/**
 * Busca full-text dentro do corpo das mensagens, em todos os canais.
 *
 * Diferente da busca da lista de conversas, que olha nome, telefone, protocolo
 * e a ÚLTIMA mensagem. Esta entra no histórico inteiro — é a que responde
 * "onde foi que a gente falou de troca de titularidade com esse cliente?".
 *
 * A RPC roda com a permissão de quem chamou (sem `security definer`), então a
 * RLS continua valendo: agente não acha conversa que está com outra pessoa.
 */
export async function buscarMensagens(orgId: string, termo: string, limite = 40): Promise<AchadoBusca[]> {
  const t = termo.trim();
  if (t.length < 2) return [];
  const { data, error } = await supabase.rpc('inbox_buscar_mensagens', {
    p_org_id: orgId,
    p_termo: t,
    p_limite: limite,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as AchadoBusca[];
}

// ─── Relatório ──────────────────────────────────────────────────────────────

export interface LinhaRelatorio {
  channel_type: ChannelType;
  responsavel_id: string | null;
  responsavel_nome: string;
  conversas: number;
  respondidas: number;
  mediana_minutos: number | null;
  media_minutos: number | null;
  ate_15_min: number;
  acima_de_1h: number;
}

export async function carregarRelatorio(
  orgId: string,
  desde: string,
  ate: string,
): Promise<LinhaRelatorio[]> {
  const { data, error } = await supabase.rpc('inbox_relatorio_primeira_resposta', {
    p_org_id: orgId,
    p_desde: desde,
    p_ate: ate,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as LinhaRelatorio[];
}

// ─── Audit log ──────────────────────────────────────────────────────────────

export interface LinhaAuditoria {
  id: number;
  actor_id: string | null;
  action: string;
  entity_id: string | null;
  diff: Record<string, unknown> | null;
  created_at: string;
  ator_nome?: string | null;
}

/** Só supervisor e admin leem — a policy de `inbox_audit_log` exige `ve_tudo`. */
export async function carregarAuditoria(orgId: string, limite = 50): Promise<LinhaAuditoria[]> {
  const { data, error } = await supabase
    .from('inbox_audit_log')
    .select('id, actor_id, action, entity_id, diff, created_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw new Error(error.message);
  return (data ?? []) as LinhaAuditoria[];
}
